/* ------------------------------------- MATERIAL UI --------------------------------------- */
import { Button, Card, Link, Stack, Typography } from '@mui/material';

/* ------------------------------------- REACT JS ------------------------------------------ */
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

import PropTypes from 'prop-types';

/* ------------------------------------- COMPONENTS ---------------------------------------- */
import Label from '../Label';
import Logo from '../Logo';
import { useTranslation } from '../../common/LocalizationProvider';
import { priceCurrency, symbolCurrency } from './utils';
import { selectSession } from '../../createSelectorUtils';

Subscription.propTypes = {
  tier: PropTypes.object,
  currency: PropTypes.string
};
export default function Subscription({ tier, currency }) {
  const t = useTranslation();
  const navigate = useNavigate();

  const session = useSelector(selectSession);

  const display = () => {
    if (tier && tier.interval && (tier.interval === 'month' || tier.interval === 'year')) {
      switch (tier.interval) {
        case 'month':
          if (tier.intervalCount === 3) {
            return {
              id: 'quarterly',
              plan: t('SPL_Name4'),
              description: t('SPL_Headline4'),
              price: priceCurrency(tier, currency),
              priceInterval: t('SPL_Price4'),
              priceIntervalCount: tier.intervalCount,
              priceID: tier.priceId,
              engagement: t(`SPL_Eng4_${currency}`),
              logoColor:
                'invert(66%) sepia(93%) saturate(729%) hue-rotate(352deg) brightness(106%) contrast(104%);',
              labelColor: 'warning',
              labelText: t('SPL_Label4'),
              buttonText: t('SPL_Button2')
            };
          }
          return {
            id: 'month',
            plan: t('SPL_Name2'),
            description: t('SPL_Headline2'),
            price: priceCurrency(tier, currency),
            priceInterval: t('SPL_Price2'),
            priceIntervalCount: tier.intervalCount,
            priceID: tier.priceId,
            engagement: t(`SPL_Eng2_${currency}`),
            logoColor:
              'invert(49%) sepia(77%) saturate(3608%) hue-rotate(334deg) brightness(101%) contrast(105%);',
            labelColor: 'error',
            labelText: t('SPL_Label2'),
            buttonText: t('SPL_Button2')
          };
        case 'year':
          if (tier.eurPrice === '100') {
            return {
              id: 'year',
              plan: t('SPL_Name3'),
              description: t('SPL_Headline5'),
              price: priceCurrency(tier, currency),
              priceInterval: t('SPL_Price3'),
              priceID: tier.priceId,
              engagement: t(`SPL_Eng5_${currency}`),
              logoColor:
                'invert(11%) sepia(78%) saturate(625%) hue-rotate(83deg) brightness(95%) contrast(102%);',
              labelColor: 'dark_green',
              labelText: t('SPL_Label5'),
              buttonText: t('SPL_Button2'),
              description2: t(`SPL_Description_Association3_${currency}`)
            };
          }

          if (tier.eurPrice === '80') {
            return {
              id: 'year',
              plan: t('SPL_Name3'),
              description: t('SPL_Headline6'),
              price: priceCurrency(tier, currency),
              priceInterval: t('SPL_Price3'),
              priceID: tier.priceId,
              engagement: t(`SPL_Eng6_${currency}`),
              logoColor:
                'invert(49%) sepia(72%) saturate(4813%) hue-rotate(84deg) brightness(97%) contrast(83%);',
              labelColor: 'success',
              labelText: t('SPL_Label6'),
              buttonText: t('SPL_Button2'),
              description2: t(`SPL_Description_Association2_${currency}`)
            };
          }

          return {
            id: 'year',
            plan: t('SPL_Name3'),
            description: t('SPL_Headline3'),
            price: priceCurrency(tier, currency),
            priceInterval: t('SPL_Price3'),
            priceID: tier.priceId,
            engagement: t(`SPL_Eng6_${currency}`),
            logoColor:
              'invert(45%) sepia(30%) saturate(5935%) hue-rotate(193deg) brightness(103%) contrast(101%);',
            labelColor: 'info',
            labelText: t('SPL_Label3'),
            buttonText: t('SPL_Button2'),
            description2: t(`SPL_Description_Association1_${currency}`)
          };
        default:
          return {};
      }
    } else {
      return {
        id: 'free',
        plan: t('SPL_Name1'),
        description: t('SPL_Headline1'),
        price: t('SPL_Price1'),
        engagement: t('SPL_Eng1'),
        logoColor:
          'invert(21%) sepia(3%) saturate(14%) hue-rotate(19deg) brightness(98%) contrast(81%);',
        labelColor: 'default',
        labelText: t('SPL_Label1'),
        buttonText: t('SPL_Button3')
      };
    }
  };

  function handlePlanSelect() {
    navigate('/subscription/payment', {
      state: {
        id: display().id,
        name: display().plan,
        color: display().labelColor,
        price: display().price,
        priceInterval: display().priceInterval,
        priceID: display().priceID,
        currency
      }
    });
  }

  return (
    <Card>
      <Stack spacing={2} padding={8} alignItems="center" justifyContent="center">
        <Logo sx={{ filter: display().logoColor }} />

        <Label color={display().labelColor}>{display().labelText}</Label>

        <Typography variant="h5">{display().name}</Typography>

        <Typography variant="h5">{display().description}</Typography>

        {display().id === 'free' ? (
          <Typography variant="h2">{display().price}</Typography>
        ) : (
          <Stack spacing={1} direction="row" alignItems="center" justifyContent="center">
            <Stack>
              <Typography variant="h3">{symbolCurrency(currency)}</Typography>
            </Stack>

            <Stack>
              <Typography variant="h2">{display().price}</Typography>
            </Stack>

            <Stack>
              <Typography variant="h4">/{display().priceInterval}</Typography>
            </Stack>
          </Stack>
        )}

        <Typography variant="h7" fontSize={14} textAlign="center">
          {display().engagement}
        </Typography>
        {display().description2 && (
          <Link
            onClick={() => window.open('https://clickandtakeoff.com/colibri-wp/aviation-verte/')}
            variant="h7"
            fontSize={14}
            textAlign="center"
          >
            {display().description2}
          </Link>
        )}

        {display().id === 'free' ? (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handlePlanSelect()}
            fullWidth
            disabled={session.expirationTime !== null}
          >
            {display().buttonText}
          </Button>
        ) : (
          <Button variant="contained" color="primary" onClick={() => handlePlanSelect()} fullWidth>
            {display().buttonText}
          </Button>
        )}
      </Stack>
    </Card>
  );
}
